import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import toastr from 'toastr';
import PublicHeader from './shared/PublicHeader';
import PublicFooter from './shared/PublicFooter';
import ShareJobDropdown from './shared/ShareJobDropdown';
import './JobPosting.css';

/**
 * Public Job Posting Page
 *
 * SEO-optimized public page for job postings
 * Accessible at: /jobs/:id
 */
const JobPosting = () => {
  const { slugOrId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    loadJobPosting();
  }, [slugOrId]);

  const loadJobPosting = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/jobs/${slugOrId}/public`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Job posting not found');
      }

      setJob(data.job);
    } catch (err) {
      console.error('Failed to load job posting:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    // Redirect to public application form
    navigate(`/jobs/${job.slug || slugOrId}/apply`);
  };

  if (loading) {
    return (
      <>
        <PublicHeader />
        <div className="job-posting-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading job posting...</p>
          </div>
        </div>
        <PublicFooter />
      </>
    );
  }

  if (error || !job) {
    return (
      <>
        <PublicHeader />
        <div className="job-posting-container">
          <div className="error-state">
            <h1>Job Not Found</h1>
            <p>{error || 'This job posting is no longer available.'}</p>
            <button className="btn-primary" onClick={() => navigate('/jobs')}>
              View All Jobs
            </button>
          </div>
        </div>
        <PublicFooter />
      </>
    );
  }

  // SEO metadata - enhanced with category-specific descriptions
  const pageTitle = `${job.title} at ${job.company || 'SecureLayer7'}`;
  const cleanDescription = (job.description || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const pageUrl = `https://sechire.net/jobs/${job.slug || slugOrId}`;
  const ogImage = 'https://securelayer7.net/images/logo.png';

  // Build rich meta description
  const metaParts = [];
  if (job.location) metaParts.push(job.location);
  if (job.workMode) metaParts.push(job.workMode.charAt(0).toUpperCase() + job.workMode.slice(1));
  if (job.employmentType) metaParts.push(job.employmentType.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase()));
  const metaPrefix = metaParts.length > 0 ? `${metaParts.join(' | ')} — ` : '';
  const pageDescription = `${metaPrefix}${cleanDescription.substring(0, 200 - metaPrefix.length)}`;

  // Build comprehensive JSON-LD
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": job.title,
    "description": cleanDescription.substring(0, 5000),
    "identifier": {
      "@type": "PropertyValue",
      "name": job.company || "SecureLayer7",
      "value": job.slug || slugOrId
    },
    "datePosted": job.createdAt ? new Date(job.createdAt).toISOString().split('T')[0] : undefined,
    "validThrough": job.applicationDeadline 
      ? new Date(job.applicationDeadline).toISOString().split('T')[0]
      : job.expiresAt 
        ? new Date(job.expiresAt).toISOString().split('T')[0]
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    "employmentType": job.employmentType?.toUpperCase().replace('-', '_') || 'FULL_TIME',
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company || "SecureLayer7",
      "sameAs": "https://securelayer7.net",
      "logo": "https://securelayer7.net/images/logo.png",
      "url": "https://securelayer7.net"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location || 'India',
        "addressCountry": "IN"
      }
    },
    "directApply": true,
    "url": pageUrl
  };

  // Add remote work
  if (job.workMode === 'remote') {
    jsonLd.jobLocationType = 'TELECOMMUTE';
  }

  // Add salary
  if (job.salaryRange && (job.salaryRange.min || job.salaryRange.max)) {
    jsonLd.baseSalary = {
      "@type": "MonetaryAmount",
      "currency": job.salaryRange.currency || "INR",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": job.salaryRange.min,
        "maxValue": job.salaryRange.max || job.salaryRange.min,
        "unitText": job.salaryRange.period === 'month' ? 'MONTH' : 'YEAR'
      }
    };
  }

  // Add qualifications and responsibilities
  if (job.requirements?.length > 0) {
    jsonLd.qualifications = job.requirements.join(', ');
  }
  if (job.responsibilities?.length > 0) {
    jsonLd.responsibilities = job.responsibilities.join(', ');
  }
  if (job.experienceLevel) {
    const expMap = { entry: 'Entry Level', mid: 'Mid Level', senior: 'Senior Level', lead: 'Lead / Principal', executive: 'Executive' };
    jsonLd.experienceRequirements = expMap[job.experienceLevel] || job.experienceLevel;
  }
  if (job.department) {
    jsonLd.industry = job.department;
  }

  return (
    <>
      {/* Enhanced SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="SecureLayer7 Careers" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={pageTitle} />
        <meta name="twitter:site" content="@securelayer7" />

        {/* Job Posting Schema (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PublicHeader />

      <main className="job-posting-container">
        {/* Header */}
        <header className="job-header">
          <div className="job-header-content">
            <div className="company-logo">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="job-title-section">
              <h1>{job.title}</h1>
              <div className="company-name">{job.company || 'SecureLayer7'}</div>
            </div>
          </div>
          <button className="btn-apply-header" onClick={handleApply} disabled={applying}>
            {applying ? 'Processing...' : 'Apply Now'}
          </button>
        </header>

        {/* Job Details */}
        <div className="job-content">
          <div className="job-main">
            {/* Quick Info */}
            <div className="job-quick-info">
              <div className="info-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{job.location}</span>
              </div>
              <div className="info-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="capitalize">{job.workMode}</span>
              </div>
              <div className="info-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="capitalize">{job.employmentType?.replace('-', ' ')}</span>
              </div>
              <div className="info-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="capitalize">{job.experienceLevel} Level</span>
              </div>
            </div>

            {/* Description */}
            <section className="job-section">
              <h2>About the Role</h2>
              <div className="job-description">
                <ReactMarkdown>{job.description}</ReactMarkdown>
              </div>
            </section>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <section className="job-section">
                <h2>Requirements</h2>
                <ul className="requirements-list">
                  {job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <section className="job-section">
                <h2>Responsibilities</h2>
                <ul className="requirements-list">
                  {job.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <section className="job-section">
                <h2>Benefits</h2>
                <ul className="benefits-list">
                  {job.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Apply Button */}
            <div className="apply-section">
              <button className="btn-apply-main" onClick={handleApply} disabled={applying}>
                {applying ? 'Processing...' : 'Apply for this Position'}
              </button>
              <p className="apply-note">
                By applying, you'll complete our security assessment to showcase your skills.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="job-sidebar">
            <div className="sidebar-card">
              <h3>Job Details</h3>
              <div className="detail-row">
                <span className="detail-label">Posted</span>
                <span className="detail-value">
                  {new Date(job.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Job Type</span>
                <span className="detail-value capitalize">{job.employmentType?.replace('-', ' ')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Experience</span>
                <span className="detail-value capitalize">{job.experienceLevel}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">{job.location}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Work Mode</span>
                <span className="detail-value capitalize">{job.workMode}</span>
              </div>
              {job.salary && (
                <div className="detail-row">
                  <span className="detail-label">Salary</span>
                  <span className="detail-value">{job.salary}</span>
                </div>
              )}
            </div>

            {/* Share */}
            <div className="sidebar-card">
              <h3>Share this Job</h3>
              <ShareJobDropdown job={job} />
            </div>

            {/* Trust / About Company */}
            <div className="sidebar-card sidebar-trust">
              <h3>About {job.company || 'SecureLayer7'}</h3>
              <p className="trust-about-text">
                Research-led offensive security company providing penetration testing, source code review, and AI security assessments to enterprises worldwide.
              </p>
              <div className="trust-mini-stats">
                <div className="trust-mini-stat">
                  <span className="trust-mini-num">1,000+</span>
                  <span className="trust-mini-label">Clients</span>
                </div>
                <div className="trust-mini-stat">
                  <span className="trust-mini-num">30+</span>
                  <span className="trust-mini-label">Countries</span>
                </div>
                <div className="trust-mini-stat">
                  <span className="trust-mini-num">10+</span>
                  <span className="trust-mini-label">Years</span>
                </div>
              </div>
              <div className="trust-tags">
                <span className="trust-tag">CREST Accredited</span>
                <span className="trust-tag">CVE Publisher</span>
                <span className="trust-tag">Black Hat USA</span>
              </div>
              <a href="https://securelayer7.net" target="_blank" rel="noopener noreferrer" className="trust-company-link">
                Visit securelayer7.net →
              </a>
            </div>
          </aside>
        </div>
      </main>

      <PublicFooter />
    </>
  );
};

export default JobPosting;
